import { useEffect, useState } from "react";
import type { ListItem } from "@app/sp-data";
import type { DataServices } from "./DataProvider";

/**
 * Valori di riferimento gestiti dai gestori negli elenchi SharePoint:
 * sedi, servizi, categorie e servizi per sede (con le aree).
 * Elenchi piccoli: letti una volta per sessione e tenuti in memoria;
 * dopo una modifica basta riaprire l'app.
 */

interface Voce {
  id: number;
  nome: string;
  ordine?: number;
}

interface Abbinamento {
  sede: string;
  servizio: string;
  area?: string;
}

export interface Configurazione {
  sedi: string[];
  servizi: string[];
  categorie: string[];
  abbinamenti: Abbinamento[];
}

const ordina = (a: Voce, b: Voce) => (a.ordine ?? 999) - (b.ordine ?? 999) || a.nome.localeCompare(b.nome, "it");

type ElencoValori = { queryAll(o: { top: number }): Promise<ListItem<{ Title?: string; Attivo?: boolean; Ordine?: number }>[]> };

async function voci(repo: ElencoValori): Promise<Voce[]> {
  const items = await repo.queryAll({ top: 500 });
  return items
    .filter((i) => i.fields.Attivo !== false && i.fields.Title?.trim())
    .map((i) => ({ id: i.id, nome: i.fields.Title!.trim(), ordine: i.fields.Ordine }))
    .sort(ordina);
}

async function carica(s: DataServices): Promise<Configurazione> {
  const [sedi, servizi, categorie, righe] = await Promise.all([
    voci(s.sedi),
    voci(s.servizi),
    voci(s.categorie),
    s.serviziPerSede.queryAll({ top: 2000 }),
  ]);
  const nomeSede = new Map(sedi.map((x) => [x.id, x.nome]));
  const nomeServizio = new Map(servizi.map((x) => [x.id, x.nome]));
  const abbinamenti: Abbinamento[] = [];
  for (const r of righe) {
    if (r.fields.Attivo === false) continue;
    // sede o servizio disattivati (o mancanti): la riga si ignora
    const sede = r.fields.Sede != null ? nomeSede.get(r.fields.Sede) : undefined;
    const servizio = r.fields.Servizio != null ? nomeServizio.get(r.fields.Servizio) : undefined;
    if (!sede || !servizio) continue;
    abbinamenti.push({ sede, servizio, area: r.fields.Area?.trim() || undefined });
  }
  return {
    sedi: sedi.map((x) => x.nome),
    servizi: servizi.map((x) => x.nome),
    categorie: categorie.map((x) => x.nome),
    abbinamenti,
  };
}

const cache = new WeakMap<DataServices, Promise<Configurazione>>();

export function loadConfigurazione(s: DataServices): Promise<Configurazione> {
  let p = cache.get(s);
  if (!p) {
    p = carica(s);
    p.catch(() => cache.delete(s)); // in caso di errore si riprova alla chiamata successiva
    cache.set(s, p);
  }
  return p;
}

export function useConfigurazione(s: DataServices) {
  const [config, setConfig] = useState<Configurazione>();
  const [error, setError] = useState<unknown>();
  useEffect(() => {
    loadConfigurazione(s).then(setConfig, setError);
  }, [s]);
  return { config, error };
}

/**
 * Servizi selezionabili per una sede, nell'ordine dell'elenco Servizi.
 * Se per la sede non c'è nessuna riga in "Servizi per sede", tutti i servizi.
 */
export function serviziPerSede(c: Configurazione, sede: string | undefined): string[] {
  if (!sede) return c.servizi;
  const presenti = new Set(c.abbinamenti.filter((a) => a.sede === sede).map((a) => a.servizio));
  return presenti.size ? c.servizi.filter((s) => presenti.has(s)) : c.servizi;
}

/** Aree tra cui scegliere per sede e servizio; vuoto = il campo Area non compare. */
export function areePer(c: Configurazione, sede: string | undefined, servizio: string | undefined): string[] {
  if (!sede || !servizio) return [];
  return [...new Set(c.abbinamenti.filter((a) => a.sede === sede && a.servizio === servizio && a.area).map((a) => a.area!))];
}
